/**
 * Serviço de Sincronização de Targets (Objetos para Exportação)
 * 
 * Implementa a estratégia de sincronização de alvos de exportação:
 * 1. targetsOverride (se fornecido explicitamente)
 * 2. monitorador-objetos:sync (sincronização com CorelDRAW)
 * 3. savedData (fallback com confirmação do usuário)
 * 
 * Registra telemetria quando fallback é usado.
 * 
 * Nota: Este arquivo usa 'any' para compatibilidade com payloads dinâmicos do CorelDRAW
 * e blocos catch vazios para logs opcionais que não devem interromper o fluxo.
 */
/* eslint-disable @typescript-eslint/no-explicit-any, no-empty */

import { sendToTerminal } from '../../../lib/terminalLogger';
import { normalizeContraction } from '../../../utils/contractionNormalizer';
import { requestConfirmation } from '../../../lib/globalConfirm';

export interface Target {
  staticId: string;
  guid: string;
  multiplicador?: number;
  contracao?: string;
  baseName?: string;
}

export interface SyncResult {
  /** Alvos para exportação */
  targets: Target[];
  
  /** Fonte de dados usada */
  source: 'override' | 'corel-sync' | 'saved-data';
  
  /** Se true, usuário confirmou uso de dados potencialmente desatualizados */
  usedStaleData: boolean;
}

export interface TargetSyncDependencies {
  /** Dados salvos da sessão */
  saved: any;
  
  /** Targets fornecidos explicitamente (override) */
  targetsOverride?: any[];
  
  /** Instância do monitorador de objetos (para comunicação com CorelDRAW) */
  monitoradorObjetos: any;
}

/**
 * Sincroniza targets de exportação com CorelDRAW ou fallback.
 * 
 * Prioridade:
 * 1. Se targetsOverride fornecido → usa direto
 * 2. Tenta sync via monitorador-objetos (CorelDRAW)
 * 3. Fallback para savedData (com confirmação do usuário)
 * 
 * @throws {Error} Se cancelado pelo usuário ou sem dados disponíveis
 */
export async function syncTargetsFromCorel(
  deps: TargetSyncDependencies
): Promise<SyncResult> {
  const { saved, targetsOverride, monitoradorObjetos } = deps;
  
  let targets: Target[] = [];
  let source: SyncResult['source'] = 'corel-sync';
  let usedStaleData = false;

  // 1. Prioridade: targetsOverride (se fornecido)
  if (Array.isArray(targetsOverride) && targetsOverride.length > 0) {
    try {
      sendToTerminal('🎯 [TARGET-SYNC] Usando targets override', { count: targetsOverride.length });
    } catch {}
    
    // Legacy override (by/value) ainda suportado no código, mas C# exige staticId+guid
    targets = targetsOverride
      .map(t => ({
        staticId: String((t as any).staticId || ''),
        guid: String((t as any).guid || ''),
        multiplicador: (t as any).multiplicador,
        contracao: (t as any).contracao
      }))
      .filter(t => t.staticId && t.guid);
    
    source = 'override';
  } else {
    // 2. Tentar sincronizar via C# (monitorar-objetos: sync)
    try {
      try {
        sendToTerminal('📥 [MONITORAR-OBJETOS] SEND sync', {
          action: 'monitorar-objetos',
          data: { comando: 'sync' }
        });
      } catch {}
      
      const syncRes = await monitoradorObjetos.sincronizarObjetos();
      
      try {
        sendToTerminal('📤 [MONITORAR-OBJETOS] RECV sync', {
          hasData: !!syncRes,
          total: Array.isArray(syncRes?.objetos) ? syncRes.objetos.length : 0
        });
      } catch {}
      
      if (syncRes && Array.isArray(syncRes?.objetos) && syncRes.objetos.length > 0) {
        const arr = syncRes.objetos as any[];
        targets = arr
          .map((o: any) => ({
            staticId: String(o?.staticId ?? o?.StaticID ?? ''),
            guid: String(o?.meta?.guid ?? o?.guid ?? o?.Guid ?? ''),
            multiplicador: (typeof o?.meta?.multiplicador === 'number') ? o.meta.multiplicador : undefined,
            contracao: (typeof o?.meta?.contracao === 'string') ? normalizeContraction(o.meta.contracao) : undefined,
          }))
          .filter(t => t.staticId && t.guid);
        
        try {
          sendToTerminal('📦 [SYNC] TARGETS_READY', { count: targets.length });
        } catch {}
        
        source = 'corel-sync';
      }
    } catch (syncError) {
      try {
        sendToTerminal('❌ [SYNC] Erro ao executar sync', { error: String(syncError) });
      } catch {}
    }

    // 3. Fallback: savedData - COM VALIDAÇÃO E CONFIRMAÇÃO
    if (!targets.length) {
      // Importar sistema de telemetria
      const { logFallbackUsage, FallbackCategory, FallbackRisk } = await import('../../../lib/fallbackMonitor');
      
      // Logar uso de fallback
      logFallbackUsage({
        category: FallbackCategory.BUSINESS_DATA,
        risk: FallbackRisk.HIGH,
        location: 'ExportModal:targets',
        primarySource: 'monitorador-objetos:sync',
        fallbackSource: 'savedData',
        reason: 'Sync do CorelDRAW falhou ou retornou vazio',
        timestamp: Date.now()
      });

      const objects: Array<{
        staticId?: string;
        id?: string;
        nome?: string;
        quantity?: number;
        contraction?: string | null;
        guid?: string;
      }> = saved?.objetos || [];
      
      // Se não tem nem dados salvos, falhar completamente
      if (objects.length === 0) {
        alert(
          'Erro: Não foi possível sincronizar objetos com o CorelDRAW e não há dados salvos.\n\n' +
          'Verifique se:\n' +
          '- O CorelDRAW está respondendo\n' +
          '- Há objetos selecionados no documento\n' +
          '- O documento foi salvo previamente\n\n' +
          'Ação necessária: Salve o documento primeiro.'
        );
        throw new Error('Nenhum target disponível para exportação');
      }

      // Pedir confirmação explícita do usuário
      const usarDadosSalvos = await requestConfirmation({
        title: 'Falha ao sincronizar com CorelDRAW',
        message:
          '⚠️ ATENÇÃO: Falha ao sincronizar com CorelDRAW\n\n' +
          'Deseja continuar usando dados salvos anteriormente?\n\n' +
          `⚠️ Os objetos podem estar DESATUALIZADOS (${objects.length} objeto${objects.length !== 1 ? 's' : ''} salvo${objects.length !== 1 ? 's' : ''}).\n\n` +
          'Recomendamos:\n' +
          '- CANCELAR e tentar novamente\n' +
          '- Salvar o documento novamente antes de exportar\n' +
          '- Verificar se o CorelDRAW está respondendo',
        confirmLabel: 'Usar Dados Salvos',
        cancelLabel: 'Cancelar'
      });
      
      if (!usarDadosSalvos) {
        try {
          sendToTerminal('🚫 [EXPORT] Usuário cancelou uso de dados salvos');
        } catch {}
        throw new Error('Exportação cancelada pelo usuário');
      }

      try {
        sendToTerminal('⚠️ [EXPORT] Usuário optou por usar dados salvos', {
          count: objects.length,
          warning: 'Dados podem estar desatualizados'
        });
      } catch {}

      targets = objects
        .map((o) => ({
          staticId: String(o.staticId || o.id || ''),
          guid: String(o.guid || ''),
          ...(typeof o.quantity === 'number' ? { multiplicador: o.quantity } : {}),
          ...(o.contraction ? { contracao: o.contraction } : {}),
        }))
        .filter(t => t.staticId && t.guid);
      
      source = 'saved-data';
      usedStaleData = true;
    }
  }

  return {
    targets,
    source,
    usedStaleData
  };
}

